import { useEffect, useRef, useState } from 'react';
import { cabinetNav } from './cabinet.mock';
import { CabinetView, SubscriptionPlan, WalletInfo } from './cabinet.schemas';
import { PaymentModal } from './components/PaymentModal';
import { useDashboardState } from './useDashboardState';
import { DashboardPage } from './pages/DashboardPage';
import { AccountPage } from './pages/AccountPage';
import { DevicesPage } from './pages/DevicesPage';
import { ImportPage } from './pages/ImportPage';
import { ShopPage } from './pages/ShopPage';
import { WalletPage } from './pages/WalletPage';
import './CabinetApp.css';

type CabinetAppProps = {
  canConnectTelegram?: boolean;
  isTelegramLinked?: boolean;
  telegramUsername?: string;
  userEmail?: string;
  onBackToLanding: () => void;
  onConnectTelegram?: () => Promise<string>;
  onLogout: () => void;
};

export function CabinetApp({
  canConnectTelegram = false,
  isTelegramLinked = false,
  telegramUsername = '',
  userEmail,
  onBackToLanding,
  onConnectTelegram,
  onLogout,
}: CabinetAppProps) {
  const [activeView, setActiveView] = useState<CabinetView>('dashboard');
  const previousViewRef = useRef<CabinetView>('dashboard');
  const contentRef = useRef<HTMLElement | null>(null);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [selectedWallet, setSelectedWallet] = useState<WalletInfo | null>(null);
  const [telegramStatus, setTelegramStatus] = useState('');
  const [isTelegramConnecting, setIsTelegramConnecting] = useState(false);
  const { dashboardState, hasLoaded, isLoading, refreshDashboard, setDashboardState } = useDashboardState(true);
  const displayTelegramUsername = telegramUsername ? `@${telegramUsername.replace(/^@/, '')}` : '';

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [activeView]);

  useEffect(() => {
    if (activeView === 'dashboard' && previousViewRef.current !== 'dashboard') {
      refreshDashboard();
    }

    previousViewRef.current = activeView;
  }, [activeView, refreshDashboard]);

  const renderPage = () => {
    switch (activeView) {
      case 'dashboard':
        return (
          <DashboardPage
            dashboardState={dashboardState}
            isLoading={isLoading}
            canConnectTelegram={canConnectTelegram}
            isTelegramLinked={isTelegramLinked}
            isTelegramConnecting={isTelegramConnecting}
            telegramStatus={telegramStatus}
            telegramUsername={telegramUsername}
            onConnectTelegram={handleConnectTelegram}
            onRefreshDashboard={refreshDashboard}
            setDashboardState={setDashboardState}
            onSelectView={setActiveView}
          />
        );
      case 'shop':
        return <ShopPage onOpenPayment={(plan, wallet) => {
          setSelectedPlan(plan);
          setSelectedWallet(wallet);
          setIsPaymentOpen(true);
        }} />;
      case 'devices':
        return <DevicesPage dashboardState={dashboardState} setDashboardState={setDashboardState} onSelectView={setActiveView} />;
      case 'wallet':
        return <WalletPage />;
      case 'account':
        return (
          <AccountPage
            canConnectTelegram={canConnectTelegram}
            isTelegramLinked={isTelegramLinked}
            isTelegramConnecting={isTelegramConnecting}
            telegramStatus={telegramStatus}
            telegramUsername={telegramUsername}
            userEmail={userEmail}
            onConnectTelegram={handleConnectTelegram}
            onLogout={onLogout}
          />
        );
      case 'import':
        return <ImportPage onSelectView={setActiveView} />;
    }
  };

  const getMobileNavLabel = (view: CabinetView) => {
    const labels: Record<CabinetView, string> = {
      dashboard: 'Главная',
      shop: 'Магазин',
      devices: 'Устр.',
      wallet: 'Баланс',
      account: 'ЛК',
      import: 'Импорт',
    };

    return labels[view];
  };

  const mobileNavItems = [
    cabinetNav.find((item) => item.id === 'shop'),
    cabinetNav.find((item) => item.id === 'devices'),
    cabinetNav.find((item) => item.id === 'dashboard'),
    cabinetNav.find((item) => item.id === 'wallet'),
    cabinetNav.find((item) => item.id === 'account'),
  ].filter(Boolean) as typeof cabinetNav;

  const handleConnectTelegram = async () => {
    if (!onConnectTelegram || isTelegramConnecting) {
      return;
    }

    setTelegramStatus('');
    setIsTelegramConnecting(true);

    try {
      const message = await onConnectTelegram();
      setTelegramStatus(message);
    } catch (error) {
      setTelegramStatus(error instanceof Error ? error.message : 'Не удалось связать Telegram');
    } finally {
      setIsTelegramConnecting(false);
    }
  };

  return (
    <main className="cabinet-app">
      <div className="background-glow" />
      <aside className="cabinet-sidebar">
        <button className="cabinet-brand" type="button" onClick={onBackToLanding}>MifVPN</button>
        <nav className="cabinet-nav" aria-label="Навигация личного кабинета">
          {cabinetNav.map((item) => (
            <button className={activeView === item.id ? 'active' : ''} type="button" onClick={() => setActiveView(item.id)} key={item.id}>
              {item.label}
            </button>
          ))}
        </nav>
        <div className="cabinet-user">
          <strong>{userEmail || '@mif_user'}</strong>
          <span>{isTelegramLinked ? displayTelegramUsername || 'Telegram подключен' : 'Личный кабинет'}</span>
          {!isTelegramLinked && canConnectTelegram ? (
            <button className="telegram-connect-action" type="button" onClick={handleConnectTelegram}>
              {isTelegramConnecting ? 'Связываем...' : 'Связать Telegram'}
            </button>
          ) : null}
          {telegramStatus ? <span className="telegram-link-status">{telegramStatus}</span> : null}
          <button type="button" onClick={onLogout}>Выйти</button>
        </div>
      </aside>
      <section className="cabinet-content" ref={contentRef}>
        <div className="cabinet-mobile-top">
          <button className="cabinet-brand" type="button" onClick={onBackToLanding}>MifVPN</button>
          <div className="cabinet-mobile-top-actions">
            {!isTelegramLinked && canConnectTelegram ? (
              <button className="cabinet-mobile-telegram" type="button" onClick={handleConnectTelegram}>
                {isTelegramConnecting ? '...' : 'TG'}
              </button>
            ) : null}
            <button className="cabinet-mobile-logout" type="button" onClick={onLogout}>Выйти</button>
          </div>
        </div>
        {isLoading && hasLoaded ? (
          <div className="cabinet-loading-banner" role="status" aria-live="polite">
            <span aria-hidden="true" />
            <strong>Загружаем данные аккаунта</strong>
          </div>
        ) : null}
        {hasLoaded ? renderPage() : (
          <section className="cabinet-loading-panel" role="status" aria-live="polite">
            <span aria-hidden="true" />
            <strong>Загружаем данные аккаунта</strong>
          </section>
        )}
      </section>
      <nav className="cabinet-mobile-nav" aria-label="Мобильная навигация личного кабинета">
        {mobileNavItems.map((item) => (
          <button className={activeView === item.id ? 'active' : ''} type="button" onClick={() => setActiveView(item.id)} key={item.id}>
            <span aria-hidden="true" />
            <strong>{getMobileNavLabel(item.id)}</strong>
          </button>
        ))}
      </nav>
      <PaymentModal plan={selectedPlan} wallet={selectedWallet} isOpen={isPaymentOpen} onClose={() => setIsPaymentOpen(false)} />
    </main>
  );
}
